import { useState } from 'react';
import { getFields, setFields as saveFields, getLeadTypes, setLeadTypes as saveTypes, DEFAULT_FIELDS } from './leadFields';

const FIELD_TYPES = ['text', 'number', 'date', 'dropdown', 'checkbox'];
const EMPTY_FIELD = { label: '', type: 'text', required: false, options: '' };

export function LeadCustomFields() {
  const [fields, setFields] = useState(getFields);
  const [types, setTypes] = useState(getLeadTypes);
  const [typeInput, setTypeInput] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newField, setNewField] = useState(EMPTY_FIELD);
  const [toast, setToast] = useState('');

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };
  const commit = (next) => { setFields(next); saveFields(next); };

  const addField = () => {
    const label = newField.label.trim();
    if (!label) return flash('Field label is required');
    if (fields.some(f => f.label.toLowerCase() === label.toLowerCase())) return flash('A field with this name already exists');
    const options = newField.type === 'dropdown' ? newField.options.split(',').map(o => o.trim()).filter(Boolean) : [];
    if (newField.type === 'dropdown' && options.length < 2) return flash('Dropdown needs at least 2 options, separated by commas');
    commit([...fields, { id: 'c_' + Date.now(), label, type: newField.type, required: newField.required, options, system: false }]);
    setNewField(EMPTY_FIELD);
    setShowAdd(false);
    flash('✅ Field saved');
  };

  const removeField = (id) => {
    const f = fields.find(x => x.id === id);
    if (!f || !window.confirm('Delete field "' + f.label + '"?')) return;
    commit(fields.filter(x => x.id !== id));
    flash('🗑 Field removed');
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return;
    const f = [...fields];
    [f[idx], f[j]] = [f[j], f[idx]];
    commit(f);
  };

  const resetAll = () => {
    if (!window.confirm('Remove all custom fields and restore the default order?')) return;
    commit(DEFAULT_FIELDS.map(f => ({ ...f })));
    flash('↩️ Restored to defaults');
  };

  const addType = () => {
    const t = typeInput.trim();
    if (!t) return;
    if (types.some(x => x.toLowerCase() === t.toLowerCase())) return flash('This lead type already exists');
    const next = [...types, t];
    setTypes(next); saveTypes(next); setTypeInput('');
    flash('✅ Lead type added');
  };
  const removeType = (t) => { const next = types.filter(x => x !== t); setTypes(next); saveTypes(next); };

  const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none', marginBottom: 10 };
  const card = { background: '#fff', borderRadius: 14, border: '1px solid #eef0f2', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: 16 };
  const customCount = fields.filter(f => !f.system).length;

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>⚙️ Custom Lead Fields</div>
          <div style={{ fontSize: 12, color: '#aaa', marginTop: 3 }}>Reorder fields, add custom fields and lead types</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {customCount > 0 && <button onClick={resetAll} style={{ padding: '8px 14px', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Reset</button>}
          <button onClick={() => setShowAdd(true)} style={{ padding: '8px 16px', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Field</button>
        </div>
      </div>

      <div style={card}>
        {fields.map((f, idx) => (
          <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f8f8f8' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button onClick={() => move(idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#ddd' : '#888', fontSize: 12, padding: 0, lineHeight: 1 }}>▲</button>
              <button onClick={() => move(idx, 1)} disabled={idx === fields.length - 1} style={{ background: 'none', border: 'none', cursor: idx === fields.length - 1 ? 'default' : 'pointer', color: idx === fields.length - 1 ? '#ddd' : '#888', fontSize: 12, padding: 0, lineHeight: 1 }}>▼</button>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
                {f.label}
                {f.required && <span style={{ color: '#f44336', marginLeft: 4 }}>*</span>}
                {f.system && <span style={{ fontSize: 10, color: '#aaa', marginLeft: 6, background: '#f5f5f5', padding: '1px 6px', borderRadius: 4 }}>system</span>}
              </div>
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.type}{f.options && f.options.length ? ' · ' + f.options.join(', ') : ''}
              </div>
            </div>
            {!f.system && (
              <button onClick={() => removeField(f.id)} style={{ background: 'none', border: 'none', color: '#f44336', fontSize: 16, cursor: 'pointer', padding: '4px 8px' }}>✕</button>
            )}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>🏷️ Lead Types</div>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 10 }}>Shown as a dropdown in the Add Lead form (e.g. Retail, Wholesale, Enquiry)</div>
      <div style={{ ...card, padding: 14 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: types.length ? 12 : 0 }}>
          <input style={{ ...inp, marginBottom: 0, flex: 1 }} placeholder="New lead type" value={typeInput} onChange={e => setTypeInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addType()} />
          <button onClick={addType} style={{ padding: '8px 16px', background: '#075e54', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
        </div>
        {types.length === 0 && <div style={{ fontSize: 12, color: '#bbb', marginTop: 10 }}>No lead types yet</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {types.map(t => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f0faf5', border: '1px solid #cdeedd', color: '#075e54', borderRadius: 20, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
              {t}
              <span onClick={() => removeType(t)} style={{ cursor: 'pointer', color: '#c62828', fontSize: 14, lineHeight: 1 }}>×</span>
            </span>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>Preview mode: saved in this browser only. Server sync comes with the backend.</div>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowAdd(false)}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 380, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 16 }}>+ New Custom Field</div>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Field Label *</label>
            <input style={{ ...inp, marginTop: 6 }} placeholder="e.g. Budget, City, Reference" value={newField.label} onChange={e => setNewField({ ...newField, label: e.target.value })} />
            <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Field Type</label>
            <select style={{ ...inp, marginTop: 6 }} value={newField.type} onChange={e => setNewField({ ...newField, type: e.target.value })}>
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {newField.type === 'dropdown' && (
              <>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>Options (comma separated)</label>
                <input style={{ ...inp, marginTop: 6 }} placeholder="e.g. Cash, UPI, Card" value={newField.options} onChange={e => setNewField({ ...newField, options: e.target.value })} />
              </>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#555', cursor: 'pointer', marginBottom: 16 }}>
              <input type="checkbox" checked={newField.required} onChange={e => setNewField({ ...newField, required: e.target.checked })} />
              Required field
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px 0', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={addField} style={{ flex: 1, padding: '10px 0', background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Add Field</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1a1a2e', color: '#fff', padding: '12px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 2000 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
